import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import {injectIntl, intlShape, defineMessages} from 'react-intl';

import DragConstants from '../../lib/drag-constants';
import {STAGE_DISPLAY_SIZES} from '../../lib/layout-constants.js';

import Box from '../box/box.jsx';
import SpriteSelectorItem from '../../containers/sprite-selector-item.jsx';
import SpriteSelectorAdd from '../sprite-selector-add/sprite-selector-add.jsx';
import SortableHOC from '../../lib/sortable-hoc.jsx';
import SortableAsset from '../asset-panel/sortable-asset.jsx';

import styles from './sprite-selector.css';

const messages = defineMessages({
    stage: {
        defaultMessage: 'Stage',
        description: 'Stage target title',
        id: 'gui.stageSelector.stage'
    }
});

const SpriteList = function (props) {
    const {
        containerRef,
        editingTarget,
        draggingIndex,
        draggingType,
        hoveredTarget,
        onConfigSpriteClick,
        onDeleteSprite,
        onDuplicateSprite,
        onExportSprite,
        onNewSpriteClick,
        onSelectSprite,
        onAddSortable,
        onRemoveSortable,
        ordering,
        raised,
        selectedId,
        stage,
        stageSize,
        items,
        intl
    } = props;

    const isSpriteDrag = draggingType === DragConstants.SPRITE;

    const AddSpriteItem = (
        <div
            className={classNames(styles.spriteWrapper, {
                [styles.spriteWrapperSmall]: stageSize === STAGE_DISPLAY_SIZES.small
            })}
            style={{
                order: items.length
            }}
        >
            <SpriteSelectorAdd
                className={styles.sprite}
                onClick={onNewSpriteClick}
            />
        </div>);

    const BackdropItem = (
        <div
            className={classNames(styles.spriteWrapper, {
                [styles.spriteWrapperSmall]: stageSize === STAGE_DISPLAY_SIZES.small
            })}
            style={{
                order: -1
            }}
        >
            <SpriteSelectorItem
                isStage
                asset={stage.costume && stage.costume.asset}
                assetId={stage.costume && stage.costume.assetId}
                className={classNames(styles.sprite, {
                    [styles.isSelected]: stage.id === selectedId,
                    [styles.raised]: raised,
                    [styles.receivedBlocks]:
                    hoveredTarget.sprite === stage.id &&
                    stage.id !== editingTarget &&
                    hoveredTarget.receivedBlocks
                })}
                id={stage.id}
                name={intl.formatMessage(messages.stage)}
                selected={stage.id === selectedId}
                onClick={onSelectSprite}
                onConfigClick={onConfigSpriteClick}
            />
        </div>

    );
    return (
        <Box
            className={styles.itemsWrapper}
            componentRef={containerRef}
        >
            {BackdropItem}
            {items.map((sprite, index) => {

                // If the sprite has just received a block drop, used for green highlight
                const receivedBlocks = (
                    hoveredTarget.sprite === sprite.id &&
                    sprite.id !== editingTarget &&
                    hoveredTarget.receivedBlocks
                );

                // If the sprite is indicating it can receive block dropping, used for blue highlight
                let isRaised = !receivedBlocks && raised && sprite.id !== editingTarget;
                const isSprite = !sprite.isStage;

                // A sprite is also raised if a costume or sound is being dragged.
                // Note the absence of the self-sharing check: a sprite can share assets with itself.
                // This is a quirk of 2.0, but seems worth leaving possible, it
                // allows quick (albeit unusual) duplication of assets.
                isRaised = isRaised || draggingType === DragConstants.COSTUME ||
                    draggingType === DragConstants.SOUND;
                let assetName = '';
                if (isSprite) {
                    assetName = sprite.name;
                } else {
                    assetName = intl.formatMessage(messages.stage);
                }

                return (
                    <SortableAsset
                        className={classNames(styles.spriteWrapper, {
                            [styles.placeholder]: isSpriteDrag && index === draggingIndex,
                            [styles.spriteWrapperSmall]: stageSize === STAGE_DISPLAY_SIZES.small
                        })}
                        index={isSpriteDrag ? ordering.indexOf(index) : index}
                        key={sprite.name}
                        onAddSortable={onAddSortable}
                        onRemoveSortable={onRemoveSortable}
                    >
                        <SpriteSelectorItem
                            asset={sprite.costume && sprite.costume.asset}
                            assetId={sprite.costume && sprite.costume.assetId}
                            className={classNames(styles.sprite, {
                                [styles.raised]: isRaised,
                                [styles.receivedBlocks]: receivedBlocks,
                                [styles.isSelected]: sprite.id === selectedId
                            })}
                            // 是否锁住角色
                            disabled={sprite.disabled}
                            // 是否显示角色
                            show={sprite.status === 'SHOW'}
                            dragPayload={sprite}
                            dragType={DragConstants.SPRITE}
                            id={sprite.id}
                            index={index}
                            isSprite={isSprite}
                            isStage={sprite.isStage}
                            key={sprite.id}
                            name={assetName}
                            selected={sprite.id === selectedId}
                            onClick={onSelectSprite}
                            onConfigClick={onConfigSpriteClick}
                            onDeleteButtonClick={onDeleteSprite}
                            onDuplicateButtonClick={onDuplicateSprite}
                            onExportButtonClick={onExportSprite}
                        />
                    </SortableAsset>

                );
            })}
            {AddSpriteItem}
        </Box>
    );
};

const spriteShape = PropTypes.shape({
    costume: PropTypes.shape({
        url: PropTypes.string,
        name: PropTypes.string.isRequired,
        // The following are optional because costumes uploaded from disk
        // will not have these properties available
        bitmapResolution: PropTypes.number,
        rotationCenterX: PropTypes.number,
        rotationCenterY: PropTypes.number
    }),
    direction: PropTypes.number,
    id: PropTypes.string,
    name: PropTypes.string,
    order: PropTypes.number,
    size: PropTypes.number,
    visibility: PropTypes.bool,
    x: PropTypes.number,
    y: PropTypes.number
});
SpriteList.propTypes = {
    containerRef: PropTypes.func,
    draggingIndex: PropTypes.number,
    draggingType: PropTypes.oneOf(Object.keys(DragConstants)),
    editingTarget: PropTypes.string,
    hoveredTarget: PropTypes.shape({
        hoveredSprite: PropTypes.string,
        receivedBlocks: PropTypes.bool
    }),
    intl: intlShape.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({
        costume: PropTypes.shape({
            url: PropTypes.string,
            name: PropTypes.string.isRequired,
            bitmapResolution: PropTypes.number.isRequired,
            rotationCenterX: PropTypes.number.isRequired,
            rotationCenterY: PropTypes.number.isRequired
        }),
        name: PropTypes.string.isRequired,
        order: PropTypes.number.isRequired
    })),
    onAddSortable: PropTypes.func,
    onConfigSpriteClick: PropTypes.func,
    onDeleteSprite: PropTypes.func,
    onDuplicateSprite: PropTypes.func,
    onExportSprite: PropTypes.func,
    onNewSpriteClick: PropTypes.func,
    onRemoveSortable: PropTypes.func,
    onSelectSprite: PropTypes.func,
    ordering: PropTypes.arrayOf(PropTypes.number),
    raised: PropTypes.bool,
    selectedId: PropTypes.string,
    stage: spriteShape,
    stageSize: PropTypes.number
};

export default injectIntl(SortableHOC(SpriteList));
